<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores, $bus } = proxy;

const router = useRouter();
const cartStore = $stores?.cart;
const userStore = $stores?.user;

const mobileMenuOpen = ref(false);
const cartItemsCount = computed(() => cartStore?.getItemCount() || 0);
const cartTotal = computed(() => cartStore?.state?.total || 0);
const currentUser = computed(() => userStore?.state?.current);
const isLoggedIn = computed(
  () => userStore?.state?.isLoggedIn || !!currentUser.value
);

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const goToCart = () => {
  router.push('/cart');
};

const handleLogout = () => {
  $bus?.emit('user.logout');
  mobileMenuOpen.value = false;
};
</script>

<template>
  <div
    class="h-16"
    :class="{
      'h-0! opacity-0 pointer-events-none':
        /dashboard/i.test($route.path) ||
        ['/register', '/login'].includes($route.path)
    }"
  ></div>

  <header
    class="fixed w-full top-0 z-50"
    :class="{
      'h-0 opacity-0 pointer-events-none':
        /dashboard/i.test($route.path) ||
        ['/register', '/login'].includes($route.path)
    }"
    style="
      background: rgba(10, 10, 11, 0.85);
      backdrop-filter: blur(20px) saturate(180%);
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    "
  >
    <!-- Mobile Header -->
    <div class="lg:hidden">
      <div class="flex items-center justify-between px-4 py-4">
        <RouterLink to="/" class="flex items-center gap-2">
          <img
            src="/kingrecharges-logo.svg"
            alt="Kingrecharges Store"
            class="w-10 h-10"
          />
          <span class="font-bold text-base text-white">LacasaeStore</span>
        </RouterLink>

        <div class="flex items-center gap-3">
          <button
            @click="goToCart"
            class="relative p-2 text-white/70 hover:text-white transition-colors"
          >
            <span class="material-icons-outlined">shopping_cart</span>
            <span
              v-if="cartItemsCount > 0"
              class="absolute -top-1 -right-1 text-white text-xs rounded-full flex items-center justify-center"
              style="
                background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);
                min-width: 20px;
                height: 20px;
              "
            >
              {{ cartItemsCount }}
            </span>
          </button>
          <button
            @click="toggleMobileMenu"
            class="p-2 text-white/70 hover:text-white transition-colors"
          >
            <span class="material-icons-outlined">{{
              mobileMenuOpen ? 'close' : 'menu'
            }}</span>
          </button>
        </div>
      </div>

      <!-- Mobile Menu -->
      <div
        v-if="mobileMenuOpen"
        class="px-4 py-4 space-y-3"
        style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
      >
        <RouterLink
          to="/"
          class="block py-3 text-white/70 hover:text-white transition-colors"
          @click="mobileMenuOpen = false"
          >Accueil</RouterLink
        >
        <RouterLink
          to="/boutique"
          class="block py-3 text-white/70 hover:text-white transition-colors"
          @click="mobileMenuOpen = false"
          >Boutique</RouterLink
        >
        <RouterLink
          v-if="isLoggedIn"
          to="/dashboard"
          class="block py-3 text-white/70 hover:text-white transition-colors"
          @click="mobileMenuOpen = false"
          >Mon Espace</RouterLink
        >

        <div
          v-if="!isLoggedIn"
          class="pt-2"
          style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
        >
          <RouterLink
            to="/login"
            class="block py-3 text-white/70 hover:text-white transition-colors"
            @click="mobileMenuOpen = false"
            >Connexion</RouterLink
          >
          <RouterLink
            to="/register"
            class="block py-3 text-white/70 hover:text-white transition-colors"
            @click="mobileMenuOpen = false"
            >Inscription</RouterLink
          >
        </div>
        <div
          v-else
          class="pt-2"
          style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
        >
          <button
            @click="handleLogout"
            class="w-full text-left py-3 text-white/70 hover:text-white transition-colors"
          >
            Déconnexion
          </button>
        </div>

        <div
          class="text-xs text-white/50 pt-2"
          style="border-top: 1px solid rgba(255, 255, 255, 0.06)"
        >
          Produits digitaux premium à des prix imbattables
        </div>
      </div>
    </div>

    <!-- Desktop Header -->
    <div class="hidden lg:block">
      <!-- Main Header -->
      <div class="max-w-screen-2xl mx-auto px-6 lg:px-8">
        <div class="flex items-center justify-between h-20">
          <!-- Logo -->
          <RouterLink to="/" class="flex items-center gap-3">
            <img
              src="/kingrecharges-logo.svg"
              alt="Kingrecharges Store"
              class="w-12 h-12"
            />
            <div class="flex flex-col">
              <span
                class="font-bold text-xl text-white"
                style="font-family: 'Outfit', sans-serif"
                >LacasaeStore</span
              >
              <span class="text-xs text-white/50">Premium Store</span>
            </div>
          </RouterLink>

          <!-- Navigation -->
          <nav class="flex items-center gap-2">
            <RouterLink
              to="/"
              class="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300"
            >
              <span class="material-icons-outlined text-lg">home</span>
              <span class="font-medium">Accueil</span>
            </RouterLink>
            <RouterLink
              to="/boutique"
              class="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300"
            >
              <span class="material-icons-outlined text-lg">shopping_bag</span>
              <span class="font-medium">Boutique</span>
            </RouterLink>
            <RouterLink
              v-if="isLoggedIn"
              to="/dashboard"
              class="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300"
            >
              <span class="material-icons-outlined text-lg">dashboard</span>
              <span class="font-medium">Mon Espace</span>
            </RouterLink>
          </nav>

          <!-- Right Actions -->
          <div class="flex items-center gap-3">
            <!-- Auth Buttons (when not logged in) -->
            <template v-if="!isLoggedIn">
              <RouterLink
                to="/login"
                class="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white/70 hover:text-white hover:bg-white/5 transition-all duration-300"
              >
                <span class="material-icons-outlined text-lg">login</span>
                <span class="font-medium">Connexion</span>
              </RouterLink>
              <RouterLink
                to="/register"
                class="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-105"
                style="
                  background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%);
                  box-shadow: 0 4px 15px rgba(55, 182, 233, 0.3);
                "
              >
                <span class="font-medium">S'inscrire</span>
              </RouterLink>
            </template>

            <!-- User Menu (when logged in) -->
            <template v-else>
              <div
                class="flex items-center gap-3 px-4 py-2 rounded-xl"
                style="
                  background: rgba(255, 255, 255, 0.05);
                  border: 1px solid rgba(255, 255, 255, 0.06);
                "
              >
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                  style="
                    background: linear-gradient(
                      135deg,
                      #4b4ced 0%,
                      #8b5cf6 100%
                    );
                  "
                >
                  {{
                    currentUser?.firstName?.[0] ||
                    currentUser?.email?.[0] ||
                    '?'
                  }}
                </div>
                <span class="text-white text-sm font-medium">{{
                  currentUser?.firstName || 'Client'
                }}</span>
                <button
                  @click="handleLogout"
                  class="ml-2 text-white/50 hover:text-white transition-colors"
                  title="Déconnexion"
                >
                  <span class="material-icons-outlined text-lg">logout</span>
                </button>
              </div>
            </template>

            <!-- Cart -->
            <button
              @click="goToCart"
              class="flex items-center gap-3 px-5 py-3 rounded-xl transition-all duration-300 hover:scale-105"
              style="
                background: rgba(255, 255, 255, 0.05);
                border: 1px solid rgba(255, 255, 255, 0.06);
              "
            >
              <span class="material-icons-outlined" style="color: #37b6e9"
                >shopping_cart</span
              >
              <div class="text-left">
                <div class="text-xs text-white/50">
                  {{ cartItemsCount }} article(s)
                </div>
                <div class="font-bold text-white">
                  {{ cartTotal.toLocaleString() }} CFA
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
